import React from 'react';
import {readStoredIdentity} from "../services/FirebaseService";

const styles = {
    wrapper: {
        padding: '32px',
        maxWidth: '640px',
        margin: '0 auto',
        fontFamily: 'sans-serif',
        lineHeight: 1.5,
    },
    identifier: {
        fontFamily: 'monospace',
        fontSize: '1.25em',
    },
    button: {
        marginTop: '24px',
        padding: '12px 24px',
        fontSize: '1em',
    },
};

/**
 * Shown when an automatic reload is not going to help. Usually that is a repeat
 * crash, once the reload budget is spent - but also a FIRST crash on a device
 * where `sessionStorage` is unavailable, because `recoverFromCrash` cannot tell a
 * first crash from a loop without a marker and declines to reload at all.
 *
 * The identifier is the one thing an administrator needs to pair this device from
 * Fixably, so it has to survive on screen: clearing it is what used to strand the
 * tablet as an unrecognised terminal. (True again since Core's FIX-13382 - for a
 * window before that, a removed terminal's identifier could not be paired back at
 * all, which is why this screen is worded for support rather than for an admin.)
 *
 * The boundary covers the whole app, not just the Firebase subtree, so this is
 * reached by any crash that is not recovered and must not read as a diagnosis. It
 * states the identifier and that the pairing is intact; it does not tell an
 * administrator to re-pair a device that is already paired, which is the one
 * action that would make things worse.
 *
 * That last rule is why the screen has three states rather than two. A read that
 * THREW is not a tablet that has never registered: blocked site data on an
 * already-paired device would otherwise be answered with "log in to register it",
 * which mints the duplicate terminal this work exists to remove - on the one
 * screen a person only reaches when something has already gone wrong.
 */
function BoundaryFallback() {
    // Guarded in FirebaseService: an error thrown while rendering a fallback is
    // not caught by the boundary that rendered it, so an unguarded read here
    // would replace the recovery screen with an uncaught one.
    const {available, identifier} = readStoredIdentity();

    return (
        <div style={styles.wrapper}>
            <h2>The signature app could not start</h2>
            {identifier ? (
                <p>
                    This device is registered as <span style={styles.identifier}>{identifier}</span> and
                    its pairing has been kept. Reload to try again. If this screen keeps coming back,
                    quote that identifier to support.
                </p>
            ) : available ? (
                <p>This device is not registered yet. Log in to register it.</p>
            ) : (
                <p>
                    This device's identity could not be read, so whether it is registered is
                    unknown. Do not register it again. If it is already paired, that creates a
                    second terminal. Reload, and tell support if this screen comes back.
                </p>
            )}
            <button style={styles.button} onClick={() => window.location.reload()}>Reload</button>
        </div>
    );
}

export default BoundaryFallback;
